import React, { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent.ts';
import { useContentRestrictionSubscriptionEvent } from '@confluence/editor-features/entry-points/useContentRestrictionSubscriptionEvent.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { fg } from '@confluence/feature-gating';

import {
	AnimatedDot,
	EllipsisContainer,
	FadingSpan,
	StatusIndicator,
	StyledText,
} from './BrowserIndicators.tsx';
import type {
	EditorConnectionBannerAnalyticsType,
	EditorConnectionBannerType,
} from './EditorConnectionBannerTypes.ts';
import { i18n } from './messages.ts';

const AnimatedEllipsis = () => (
	<EllipsisContainer>
		<AnimatedDot />
		<AnimatedDot />
		<AnimatedDot />
	</EllipsisContainer>
);

const EditorConnectionBannerComponent = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(
	injectIntl(
		({
			editorType,
			triggerAnalyticsEvent,
			collabEditInternetLost,
			collabEditConnectionTimedOut = false,
			willEditorDisable = false,
			contentType,
			contentId,
			isEditorToolbarHidden,
			intl: { formatMessage },
		}: EditorConnectionBannerType) => {
			const [showIndicator, setShowIndicator] = useState<boolean>(false);
			const [restrictionUpdate, setRestrictionUpdate] = useState<boolean>(false);

			// initialize offlineData with defaults
			const offlineData = useRef<EditorConnectionBannerAnalyticsType>({
				browserNetworkStatus: true,
				documentVisibilityState: '',
				connectionTimeoutStartTime: 0,
				offlineStartTime: 0,
			});

			const onRestrictionUpdated = useCallback(() => {
				triggerAnalyticsEvent('offline.banner.restriction.updated', {
					editorType,
					restrictionUpdate,
				});
				return editorType === 'classic' && setRestrictionUpdate(true);
			}, [editorType, restrictionUpdate, triggerAnalyticsEvent]);

			const onRestrictionUpdatedPubSubEvent = useCallback(() => {
				if (
					!['DUAL', 'GRAPHQL'].includes(
						StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
					) ||
					!expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false)
				) {
					onRestrictionUpdated();
				}
			}, [onRestrictionUpdated]);

			const toggleIndicator = useCallback(
				(
					indicatorToggleCondition: boolean,
					showIndicatorCallback?: () => void,
					hideIndicatorCallback?: () => void,
				) => {
					let hideTimeout: ReturnType<typeof setTimeout> | undefined;
					let delayTimeout: ReturnType<typeof setTimeout> | undefined;
					const HIDE_FLAG_TIMEOUT = 3000;
					const DELAY_TIMEOUT = 3000;
					if (indicatorToggleCondition) {
						delayTimeout = setTimeout(() => {
							if (showIndicatorCallback) {
								showIndicatorCallback();
							}
							return setShowIndicator(true);
						}, DELAY_TIMEOUT);
					} else {
						hideTimeout = setTimeout(() => {
							if (hideIndicatorCallback) {
								hideIndicatorCallback();
							}
							//hide show indicator only when both disabling conditions are false
							//to handle edge case where both banners got set to true but we want
							// to be able to show connected banner when restored
							if (!collabEditInternetLost && !collabEditConnectionTimedOut) {
								return setShowIndicator(false);
							}
						}, HIDE_FLAG_TIMEOUT);
					}
					return { hideTimeout, delayTimeout };
				},
				[collabEditConnectionTimedOut, collabEditInternetLost],
			);

			// Upon receiving a permission change pubsub event, prepare the banner to show alternative text
			// Note: Only classic (Synchrony) editor gets offline and back online due to permission changes
			usePubSubEvent({
				contentId,
				contentType,
				eventName: `restrictionUpdated:${contentType}`,
				onEvent: onRestrictionUpdatedPubSubEvent,
				skip:
					// eslint-disable-next-line confluence-feature-gating/no-preconditioning
					expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false) &&
					fg('confluence_enable_subscriptions-context-provider') &&
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
						'GRAPHQL',
			});

			useContentRestrictionSubscriptionEvent({
				contentId,
				contentType,
				callback: onRestrictionUpdated,
				subscriberId: 'EditorConnectionBannerSubscription',
			});

			useEffect(() => {
				if (!willEditorDisable) {
					// Reset restriction update boolean upon editor re-connection so next time editor goes offline it will show default messages
					setRestrictionUpdate(false);
				}
			}, [willEditorDisable]);

			useEffect(() => {
				const showIndicatorCB = () => {
					// save browser network status, start time of banner display, and document visibility state when user goes offline
					offlineData.current = {
						browserNetworkStatus: navigator.onLine,
						documentVisibilityState: document.visibilityState,
						offlineStartTime: performance.now(),
					};
					triggerAnalyticsEvent('offline.banner.show.indicator', {
						editorType,
						restrictionUpdate,
					});
				};
				const hideIndicatorCB = () => {
					if (showIndicator) {
						// if offline banner was displayed, fire analytics
						triggerAnalyticsEvent('offline.banner.displayed', {
							browserNetworkStatus: offlineData?.current.browserNetworkStatus,
							editorType,
							documentVisibilityState: offlineData?.current.documentVisibilityState,
							duration: offlineData?.current.offlineStartTime
								? performance.now() - offlineData?.current.offlineStartTime
								: 0,
						});
					}
				};
				const { hideTimeout, delayTimeout } = toggleIndicator(
					collabEditInternetLost,
					showIndicatorCB,
					hideIndicatorCB,
				);

				return () => {
					clearTimeout(hideTimeout);
					clearTimeout(delayTimeout);
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [collabEditInternetLost]);

			useEffect(() => {
				if (collabEditInternetLost) {
					return;
				}
				const showIndicatorCB = () => {
					offlineData.current = {
						browserNetworkStatus: navigator.onLine,
						documentVisibilityState: document.visibilityState,
						connectionTimeoutStartTime: performance.now(),
					};
					triggerAnalyticsEvent('collab.edit.connection.timeout.show.indicator', {
						contentId,
						contentType,
					});
				};

				const hideIndicatorCB = () => {
					if (showIndicator) {
						// if connection timeout banner was displayed, fire analytics
						triggerAnalyticsEvent('connection.timeout.banner.displayed', {
							browserNetworkStatus: offlineData?.current.browserNetworkStatus,
							editorType,
							documentVisibilityState: offlineData?.current.documentVisibilityState,
							duration: offlineData?.current.connectionTimeoutStartTime
								? performance.now() - offlineData?.current.connectionTimeoutStartTime
								: 0,
						});
					}
				};

				const { hideTimeout, delayTimeout } = toggleIndicator(
					collabEditConnectionTimedOut,
					showIndicatorCB,
					hideIndicatorCB,
				);

				return () => {
					clearTimeout(hideTimeout);
					clearTimeout(delayTimeout);
				};
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [collabEditConnectionTimedOut]);

			let bannerMessage;
			if (willEditorDisable) {
				if (restrictionUpdate) {
					bannerMessage = i18n.editorDisabledDueToPermissionChange;
				} else if (collabEditConnectionTimedOut && !collabEditInternetLost) {
					bannerMessage = i18n.collabEditConnectionTimeout;
				} else {
					bannerMessage = i18n.editorDisabled;
				}
			} else {
				bannerMessage = i18n.editorEnabled;
			}

			return (
				<StatusIndicator
					data-testid="status-indicator"
					show={showIndicator}
					showOnlineBackground={!collabEditInternetLost && !collabEditConnectionTimedOut}
					toolbarHidden={isEditorToolbarHidden}
				>
					<FadingSpan
						data-testid="status-indicator-online-text"
						role="alert"
						show={!collabEditInternetLost && !collabEditConnectionTimedOut && showIndicator}
					>
						<StatusSuccessIcon label="online-indicator" color={token('color.icon.success')} />
						<StyledText mediaMaxWidth="150px" data-testid="online-text">
							{formatMessage(i18n.backOnline)}
						</StyledText>
					</FadingSpan>
					<FadingSpan
						data-testid="status-indicator-offline-text"
						role="alert"
						show={collabEditInternetLost || collabEditConnectionTimedOut}
					>
						<StyledText data-testid="offline-text">{formatMessage(bannerMessage)}</StyledText>
						{(collabEditInternetLost || collabEditConnectionTimedOut) && <AnimatedEllipsis />}
					</FadingSpan>
				</StatusIndicator>
			);
		},
	),
);

export const EditorConnectionBanner = React.memo(EditorConnectionBannerComponent);
